import React, { type ReactNode, type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { LoadingIndicator } from "../ui";

export interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  /**
   * Extra app-level check result (setup wizard, network error, …), applied after auth
   * passes. Must be computed by the caller via a normal hook call (e.g. the app's own
   * `useSetupGuard()`) and passed down as a plain value — never as a function for this
   * component to invoke. Calling a hook indirectly through a prop hides it from React
   * Compiler's static dependency analysis, which can memoize this component using only
   * the visible inputs and never re-run the hidden hook when its own reactive state changes.
   */
  guardElement?: ReactElement | null;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  guardElement,
}: ProtectedRouteProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return <LoadingIndicator fullScreen />;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (guardElement) {
    return guardElement;
  }

  if (allowedRoles && allowedRoles.length > 0 && auth.user) {
    const userRoles = Array.isArray(auth.user.role)
      ? auth.user.role
      : [auth.user.role];
    const hasAccess = userRoles.some((role) => allowedRoles.includes(role));
    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
